import * as Yup from 'yup';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Typography,
  Button,
  useTheme,
  Divider,
  Fade,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip
} from '@mui/material';
import React from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import checkmarkCircleFill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import questionMarkCircleFill from '@iconify/icons-eva/question-mark-circle-fill';
import { LoadingButton } from '@mui/lab';
import _ from 'lodash';
import toast from 'react-hot-toast';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import jwt from 'jsonwebtoken';
import { confirmAlert } from 'react-confirm-alert';
//
import * as permohonanService from '../../../services/permohonan';
import QuestionsGenerator from '../../QuestionsGenerator';
import isEligible from '../../../utils/roleEligibleGetter';
import encrypt from '../../../utils/encryptor';
import AddAttachment from './AddAttachment';
import { getBaseURL, getAPIURL, getSiteName } from '../../../utils/envGetter';
import { fCurrency } from '../../../utils/formatNumber';
import errorLogger from '../../../utils/errorLogger';

ServicesDetailPermohonanQuestions.propTypes = {
  layanan: PropTypes.object.isRequired,
  getLayanan: PropTypes.func.isRequired,
  questions: PropTypes.array.isRequired,
  setQuestions: PropTypes.func.isRequired,
  selectedRequirement: PropTypes.object.isRequired,
  informations: PropTypes.any,
  toggleUpdate: PropTypes.any,
  setToggleUpdate: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired
};

function ServicesDetailPermohonanQuestions({
  layanan,
  getLayanan,
  questions,
  setQuestions,
  selectedRequirement,
  informations,
  toggleUpdate,
  setToggleUpdate,
  user
}) {
  const questionsID = {};
  const questionsSchema = {};
  const valuesFromAPI = layanan?.meta?.data_permohonan?.metadata?.answers;
  const valuesDefault = ['admin', 'superadmin'].includes(user?.attributes?.role)
    ? {}
    : {
        nama_perangkat_daerah: user?.attributes?.name,
        subdomain: layanan?.meta?.data_permohonan?.metadata?.subdomain,
        requestor_nama: layanan?.meta?.data_permohonan?.metadata?.tte?.name,
        requestor_provinsi: 'Daerah Istimewa Yogyakarta',
        requestor_kota: 'Yogyakarta',
        requestor_unit_kerja: 'Pemerintah Daerah Daerah Istimewa Yogyakarta',
        recommender_unit_kerja: 'Pemerintah Daerah Daerah Istimewa Yogyakarta'
      };
  const [modeTandaTangan, setModeTandaTangan] = React.useState('tte');
  const [dataDukungs, setDataDukungs] = React.useState({});

  const [signer, setSigner] = React.useState('');
  const [passphrase, setPassphrase] = React.useState('');

  questions.forEach((question) => {
    questionsID[question.id] = '';
    if (!_.isEmpty(valuesDefault)) questionsID[question.id] = valuesDefault[question.id];
    if (!_.isEmpty(valuesFromAPI)) questionsID[question.id] = valuesFromAPI[question.id];

    if (question.required) {
      questionsSchema[question.id] = Yup.string().required(`${question.label} wajib di-isi`);
    }
  });

  const QuestionSchema = Yup.object().shape(questionsSchema);
  const formik = useFormik({
    initialValues: {
      ...questionsID
    },
    enableReinitialize: true,
    validationSchema: QuestionSchema,
    onSubmit: () => {
      saveData('saved');
    }
  });

  const [isSaved, setIsSaved] = React.useState(false);
  const [isSigned, setIsSigned] = React.useState(false);
  const [isApproved, setIsApproved] = React.useState(false);
  const [isRejected, setIsRejected] = React.useState(false);

  const saveData = async (status) => {
    try {
      const body = {
        data: {
          type: 'permohonan',
          id: layanan?.attributes?.id_permohonan,
          attributes: {
            document_path: null,
            is_signed: null,
            status
          },
          meta: { ...layanan?.meta?.data_permohonan?.metadata, answers: values }
        }
      };

      if (status === 'revision') {
        body.data.meta = { ...layanan?.meta?.data_permohonan?.metadata, answers: values };
      } else {
        // hot forcing change currency format
        body.data.meta.answers.estimasi_biaya = fCurrency(body.data.meta.answers.estimasi_biaya);
      }

      toast
        .promise(permohonanService.updatePermohonanData(body), {
          loading: 'Menyimpan data ...',
          error: <b>Maaf, terjadi kesalahan</b>,
          success: <b>Data berhasil disimpan</b>
        })
        .then(() => {
          setIsSaved(true);
          setToggleUpdate(!toggleUpdate);
        });
    } catch (error) {
      toast.error(error.message);
    }

    setSubmitting(false);
    if (status === 'revision') return navigate(0, { replace: true });
  };

  React.useEffect(() => {
    if (!_.isEmpty(layanan?.meta?.data_permohonan?.metadata?.answers)) setIsSaved(true);
    if (layanan?.meta?.data_permohonan?.is_signed && layanan?.meta?.data_permohonan?.document_path)
      setIsSigned(true);
    if (layanan?.meta?.data_permohonan?.metadata?.approval?.approved) {
      setIsApproved(true);
    } else if (
      layanan?.meta?.data_permohonan?.metadata?.approval?.approved === false &&
      layanan?.meta?.data_permohonan?.document_path
    ) {
      setIsRejected(true);
    }
  }, []);

  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setSubmitting,
    setFieldValue
  } = formik;

  const theme = useTheme();
  const navigate = useNavigate();

  const previewDocument = () => {
    const accessToken = jwt.sign(
      {
        requestor: layanan?.attributes?.requestor,
        id_requirement: selectedRequirement.id,
        modeTandaTangan,
        selectedRequirement
      },
      process.env.REACT_APP_SECRET_KEY,
      {
        expiresIn: '24h'
      }
    );

    navigate(`/pdf/${layanan.id}/${accessToken}`);
  };

  const openDocument = () => {
    const origin = getAPIURL();
    const path = `${origin}/pdf?path=${layanan.meta.data_permohonan.document_path
      .replace('.', '')
      .substring(1)}&id=${layanan.meta.data_permohonan.id}&filename=${
      layanan.meta.data_permohonan.id
    }`;
    window.open(path, '_blank');
  };

  const openDocumentRekomendasi = () => {
    const origin = getAPIURL();
    const path = `${origin}/pdf?path=${layanan.rekomendasi.meta.data_permohonan.document_path
      .replace('.', '')
      .substring(1)}&id=${layanan.rekomendasi.meta.data_permohonan.id}&filename=${
      layanan.rekomendasi.meta.data_permohonan.id
    }`;
    window.open(path, '_blank');
  };

  const approveDocument = async () => {
    setSubmitting(true);
    const documentPath = layanan.meta.data_permohonan.document_path;
    const documentID = layanan.meta.data_permohonan.id;

    if (!documentID) return toast.error('Gagal mendapatkan ID dokumen');

    const signers = [];
    const signerData = signer;
    signerData.passphrase = encrypt(signerData.passphrase);
    signers.push(signerData); // array to handle multiple page posibility

    const origin = getBaseURL();
    const linkQR = `${origin}/pdfscan?id=${documentID}`;

    const body = {
      link_qr: linkQR,
      document_path: documentPath,
      id_permohonan: documentID,
      signer_nik: signer.nik,
      signer_meta_data: signers,
      sign_type: 'update',
      user_info: user
    };

    toast
      .promise(permohonanService.signUpdateDocument(body), {
        loading: 'Mohon tunggu ...',
        error: <b>Maaf, terjadi kesalahan</b>,
        success: <b>Dokumen berhasil ditandatangani</b>
      })
      .then(() => {
        setSigner({ ...signer, passphrase: '' });
        setToggleUpdate(!toggleUpdate);
        setSubmitting(false);
        setIsApproved(true);
      })
      .catch((error) => {
        setSigner({ ...signer, passphrase: '' });
        setSubmitting(false);
        errorLogger(error);
      });
  };

  const getStatusColor = (type) => {
    if (type === 'approval') {
      if (isApproved) return theme.palette.primary.main;
      if (isRejected) return theme.palette.error.main;
      return theme.palette.grey[400];
    }
  };

  const doRevision = async () => {
    confirmAlert({
      title: 'Konfirmasi',
      message:
        'Apakah Anda yakin ingin melakukan revisi? Status permohonan akan berubah menjadi REVISI dan dokumen yang sudah ditandatangi akan diganti.',
      buttons: [
        {
          label: 'Yakin',
          onClick: async () => {
            try {
              saveData('revision');
            } catch (error) {
              toast.error(error.message);
            }
          }
        },
        {
          label: 'Tidak',
          onClick: () => console.log('No')
        }
      ]
    });
  };
  // console.log('Layanan:', layanan);
  return (
    <Stack direction="column" spacing={2} divider={<Divider orientation="horizontal" flexItem />}>
      <Box pt={3}>
        <Stack direction="column" spacing={2}>
          <Typography variant="h4">{selectedRequirement.attributes.label}</Typography>
          <Stack
            spacing={1}
            direction={{ xs: 'column', sm: 'row' }}
            justifyItems="flex-start"
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon
                icon={isSaved ? checkmarkCircleFill : closeCircleFill}
                color={isSaved ? theme.palette.primary.main : theme.palette.grey[400]}
              />
              <Typography>Simpan Data</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon
                icon={isSigned ? checkmarkCircleFill : closeCircleFill}
                color={isSigned ? theme.palette.primary.main : theme.palette.grey[400]}
              />
              <Typography>Tanda Tangan (TTE/Manual)</Typography>
            </Stack>
            {!['rekomendasi', 'tte'].includes(selectedRequirement.attributes.question_id) && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Icon
                  icon={isApproved ? checkmarkCircleFill : closeCircleFill}
                  color={getStatusColor('approval')}
                />
                <Typography>Disetujui</Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack
            spacing={2}
            direction={{ sm: 'column', xs: 'column', md: 'column', lg: 'row' }}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Stack
              spacing={3}
              direction="column"
              flex={6}
              divider={<Divider orientation="horizontal" flexItem />}
            >
              <Fade in>
                <Stack direction="column" spacing={2}>
                  {!_.isEmpty(questions) &&
                    questions.map((item) => (
                      <QuestionsGenerator
                        key={item.id}
                        item={item}
                        values={values}
                        setFieldValue={setFieldValue}
                        getFieldProps={getFieldProps}
                        errors={errors}
                        touched={touched}
                        isSubmitting={isSubmitting}
                        handleSubmit={handleSubmit}
                        setSubmitting={setSubmitting}
                        dataDukungs={dataDukungs}
                        setDataDukungs={setDataDukungs}
                      />
                    ))}
                </Stack>
              </Fade>

              {!isSigned ? (
                <>
                  {isEligible('data_permohonan') && (
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={isSubmitting}
                    >
                      {isSaved ? 'Update Data' : 'Simpan Data'}
                    </LoadingButton>
                  )}
                  {isSaved && isEligible('data_permohonan') && (
                    <Stack direction="column">
                      {['tte'].includes(selectedRequirement.attributes.question_id) && (
                        <FormControl>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <FormLabel>Pilih mode tanda tangan</FormLabel>
                            <Tooltip
                              title="Dokumen permohonan harus ditandatangani terlebih dahulu oleh Kepala Dinas/Instansi baik secara elektronik maupun manual. TTE (Tanda Tangan Elektronik) dapat memudahkan Anda melakukan proses tanda tangan, tanpa harus mengunduh dokumen dan mengunggah dokumen yang telah ditandatangani."
                              arrow
                            >
                              <Icon color={theme.palette.grey[500]} icon={questionMarkCircleFill} />
                            </Tooltip>
                          </Stack>

                          <RadioGroup
                            sx={{ padding: 1 }}
                            aria-labelledby="Pilih mode tanda tangan"
                            value={modeTandaTangan}
                            name="modeTandaTangan"
                            onChange={(event) => {
                              setModeTandaTangan(event.target.value);
                            }}
                          >
                            <FormControlLabel
                              value="tte"
                              control={<Radio />}
                              label="Tanda Tangan Elektronik (TTE) - Direkomendasikan"
                            />
                            <FormControlLabel
                              value="manual"
                              control={<Radio />}
                              label="Tanda Tangan Manual"
                            />
                          </RadioGroup>
                        </FormControl>
                      )}

                      <Divider sx={{ marginBottom: 3 }} />

                      <LoadingButton
                        onClick={previewDocument}
                        fullWidth
                        size="large"
                        variant="contained"
                      >
                        Submit Tanda Tangan
                      </LoadingButton>
                    </Stack>
                  )}
                </>
              ) : (
                <>
                  <Stack direction="row" spacing={2} pl={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Icon
                        icon={isSigned ? checkmarkCircleFill : closeCircleFill}
                        width={32}
                        height={32}
                        color={isSigned ? theme.palette.primary.main : theme.palette.error.main}
                      />
                      <Typography variant="body2">
                        {isSigned ? 'Sudah ditandatangani' : 'Belum ditandatangani'}
                      </Typography>
                      <Button onClick={openDocument} disabled={!isSigned} variant="outlined">
                        Lihat Dokumen Permohonan
                      </Button>
                    </Stack>
                    {layanan.type === 'subdomain' && (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Icon
                          icon={isSigned ? checkmarkCircleFill : closeCircleFill}
                          width={32}
                          height={32}
                          color={isSigned ? theme.palette.primary.main : theme.palette.error.main}
                        />
                        <Typography variant="body2">
                          {isSigned ? 'Sudah ditandatangani' : 'Belum ditandatangani'}
                        </Typography>
                        <Button
                          onClick={openDocumentRekomendasi}
                          disabled={!isSigned}
                          variant="outlined"
                        >
                          Lihat Dokumen Rekomendasi
                        </Button>
                      </Stack>
                    )}
                  </Stack>

                  {isEligible('revisi_permohonan') && (
                    <Stack direction="row" pl={2} spacing={2} justifyContent="right">
                      <Button onClick={doRevision} color="error">
                        Revisi?
                      </Button>
                    </Stack>
                  )}
                </>
              )}
            </Stack>

            {/* THIS IS SIDE DOCUMENT APPROVAL SECTION */}
            <Box sx={{ flex: 4 }}>
              <Stack direction="column" spacing={2}>
                {!['rekomendasi', 'tte'].includes(selectedRequirement.attributes.question_id) && (
                  <>
                    {isSigned && !isApproved && isEligible('data_permohonan_approval') && (
                      <>
                        <Typography variant="h5">Approval</Typography>
                        <Stack direction="column">
                          {/* <FormControl>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <FormLabel>Pilih mode tanda tangan</FormLabel>
                              <Tooltip
                                title="Jika dokumen disetujui, silakan pilih mode tanda tangan di-bawah ini untuk menyetujui permohonan. Jika dokumen tidak disetujui, silakan menuju ke halaman feedback untuk menolak permohonan."
                                arrow
                              >
                                <Icon
                                  color={theme.palette.grey[500]}
                                  icon={questionMarkCircleFill}
                                />
                              </Tooltip>
                            </Stack>

                            <RadioGroup
                              sx={{ padding: 1 }}
                              aria-labelledby="Pilih mode tanda tangan"
                              value={modeTandaTangan}
                              name="modeTandaTangan"
                              onChange={(event) => {
                                setModeTandaTangan(event.target.value);
                              }}
                            >
                              <FormControlLabel
                                value="tte"
                                control={<Radio />}
                                label="Tanda Tangan Elektronik (TTE) - Direkomendasikan"
                              />
                              <FormControlLabel
                                value="manual"
                                control={<Radio />}
                                label="Tanda Tangan Manual"
                              />
                            </RadioGroup>
                          </FormControl> */}
                        </Stack>
                      </>
                    )}
                    {isSigned &&
                      !isApproved &&
                      modeTandaTangan === 'tte' &&
                      isEligible('approve_data_permohonan') && (
                        <Stack direction="column" spacing={2} flex={5}>
                          <Typography variant="body1">Masukkan informasi penandatangan</Typography>
                          {signer.signername && (
                            <TextField disabled="true" value={signer.signername} />
                          )}
                          {signer.nip && <TextField disabled="true" value={signer.nip} />}
                          <TextField
                            label="NIK"
                            value={signer.nik}
                            onChange={(e) => setSigner({ ...signer, nik: e.target.value })}
                          />
                          <TextField
                            type="password"
                            label="Passprhase TTE"
                            value={signer.passphrase}
                            onChange={(e) => setSigner({ ...signer, passphrase: e.target.value })}
                          />
                          <LoadingButton
                            disabled={layanan?.attributes?.status === 'draft'}
                            loading={isSubmitting}
                            variant="outlined"
                            onClick={() => approveDocument()}
                          >
                            Setujui
                          </LoadingButton>
                          <Typography variant="body2">
                            {layanan?.attributes?.status === 'draft' &&
                              '** Kategorisasi Sistem Elektronik belum diisi'}
                          </Typography>
                        </Stack>
                      )}
                    {isSigned && !isApproved && modeTandaTangan !== 'tte' && (
                      <Typography variant="body2">
                        Maaf, sistem belum mendukung tanda tangan manual, silakan menggunakan TTE
                      </Typography>
                    )}
                  </>
                )}
                <Stack direction="column" spacing={2}>
                  <Typography variant="h5">Informasi</Typography>
                  {informations.map((item, index) => (
                    <Box key={item.id}>
                      <Stack direction="row" spacing={2}>
                        <Box>
                          <Typography variant="body2">{index + 1}.</Typography>
                        </Box>
                        <Box sx={{ flex: 10 }}>
                          <Typography variant="body2">{item.label}</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
                <Stack direction="column" mt={2}>
                  <AddAttachment
                    idRequirement={layanan?.attributes?.id_permohonan}
                    user={user}
                    type="data_permohonan"
                  />
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Form>
      </FormikProvider>
    </Stack>
  );
}

export default ServicesDetailPermohonanQuestions;
